import { APP_NAME, type FollowerPreview } from '@eventping/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Eye } from 'lucide-react'
import { useState } from 'react'
import { Card, Footer, Page, SectionTitle } from '@/components/shell'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { ApiFailure, api, useLive } from '@/lib/api'
import { FollowerConversation, FollowerHeading, FollowerTimelineCard } from './link'

/**
 * "Preview as follower" (/events/<id>/preview), for the event's organizer: the follower page
 * built from the same pieces as /l/<token>, fed by GET /api/events/<id>/preview, which uses
 * the same server function as followers' views. Read-only: no message box, no channel
 * switches, no leaving; nothing here writes anything or creates a link.
 */
export function PreviewPage({ eventId }: { eventId: string }) {
  const [reveal, setReveal] = useState(false)
  const queryKey = ['preview', eventId, reveal ? 'hidden' : 'now'] as const
  const view = useQuery({
    queryKey,
    queryFn: () =>
      api<FollowerPreview>(`/api/events/${eventId}/preview${reveal ? '?reveal=hidden' : ''}`),
    placeholderData: (previous) => previous,
  })
  // The organizer's own stream: steps sent, renamed or switched show here as they happen.
  useLive(view.data ? `/api/events/${eventId}/stream` : null, ['preview', eventId])

  const banner = (
    <div
      className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 py-2"
      role="status"
      data-testid="preview-banner"
    >
      <Eye className="size-5 shrink-0 text-primary" aria-hidden />
      <p className="min-w-0 flex-1 font-medium">Preview: this is what your followers see</p>
      <Button asChild size="sm" variant="outline">
        <Link to="/events/$eventId" params={{ eventId }}>
          <ArrowLeft /> Back to editing
        </Link>
      </Button>
    </div>
  )

  if (view.isPending) return <Page header={banner}>{null}</Page>
  if (view.isError) {
    const status = view.error instanceof ApiFailure ? view.error.status : 0
    return (
      <Page header={banner}>
        <Card className="text-center">
          <h1 className="text-xl font-semibold">
            {status === 401
              ? 'Sign in to preview this event'
              : status === 404
                ? 'That event does not exist'
                : 'Something went wrong'}
          </h1>
          {status === 401 && (
            <p className="mt-2">
              <a href="/auth/login?returnTo=%2F" className="underline">
                Sign in
              </a>
            </p>
          )}
        </Card>
      </Page>
    )
  }

  const v = view.data
  const texts = v.textsEnabled
  return (
    <Page header={banner}>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: wraps a Radix Switch (a button), which it labels */}
      <label className="mb-4 flex min-h-10 items-center gap-3 rounded-xl border border-dashed px-3 py-2 text-sm text-muted-foreground">
        <Switch
          checked={reveal}
          onCheckedChange={setReveal}
          aria-label="Show hidden steps where followers will see them once sent"
        />
        Show hidden steps where followers will see them once sent
      </label>

      <FollowerHeading v={v} greeting="there" />
      <FollowerTimelineCard steps={v.steps} />
      <FollowerConversation messages={v.messages} archived={v.event.status === 'archived'} />

      <Card className="mb-4" data-testid="preview-settings">
        <SectionTitle>How you get updates</SectionTitle>
        <p className="text-muted-foreground">
          Each follower sees their own email{texts ? ' and texts' : ''} here, can switch them off or
          on, and can leave the event. Not shown in the preview.
        </p>
      </Card>
      <Footer
        texts={texts}
        note={
          texts ? `Reply STOP to any ${APP_NAME} text to stop all texts, HELP for help.` : undefined
        }
      />
    </Page>
  )
}
